/*
 * Scrolling deals.
 *
 * Advances the row one card at a time on a timer. The card list is rendered
 * twice by the section, so the wrap is done by moving the scroll position
 * back by the width of one copy: the same cards are under the viewport at
 * that moment, so nothing appears to move.
 *
 * The timer only runs when the row is on screen and the tab is in front,
 * which keeps an off-screen row from scrolling through the whole catalogue
 * while nobody is looking at it.
 */
class DealMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.dmq__track');
    if (!this.track) return;

    this.interval = parseInt(this.dataset.interval, 10) || 3000;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.visible = false;
    this.hovered = false;
    this.timer = null;

    this.onEnter = () => { this.hovered = true; };
    this.onLeave = () => { this.hovered = false; };

    if (this.dataset.pauseOnHover) {
      this.addEventListener('pointerenter', this.onEnter);
      this.addEventListener('pointerleave', this.onLeave);
      // Someone tabbing through the cards should not have the row move
      // out from under them either.
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('focusout', this.onLeave);
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        this.visible = entries[0].isIntersecting;
        this.visible ? this.start() : this.stop();
      },
      { threshold: 0.1 }
    );
    this.observer.observe(this);

    document.addEventListener('visibilitychange', () => {
      document.hidden ? this.stop() : this.start();
    });

    this.reduced.addEventListener('change', () => {
      this.reduced.matches ? this.stop() : this.start();
    });
  }

  disconnectedCallback() {
    this.stop();
    if (this.observer) this.observer.disconnect();
  }

  start() {
    if (this.timer || this.reduced.matches || document.hidden || !this.visible) return;
    this.timer = setInterval(() => this.step(), this.interval);
  }

  stop() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  /* Width of one card plus the gap either side of it. Measured rather than
     read from the setting, so a card resized in the theme editor does not
     leave the step out of register with it. */
  get stride() {
    const first = this.track.children[0];
    const second = this.track.children[1];
    if (!first) return 0;
    if (second) return second.getBoundingClientRect().left - first.getBoundingClientRect().left;
    return first.getBoundingClientRect().width;
  }

  /* One copy of the list: half the track, since the section renders it twice. */
  get loopWidth() {
    return this.track.scrollWidth / 2;
  }

  step() {
    if (this.hovered) return;

    const stride = this.stride;
    if (!stride) return;

    // Past the end of the first copy: drop back by exactly one copy with
    // smooth scrolling off, so the correction is not animated and so is
    // not seen.
    if (this.scrollLeft >= this.loopWidth) {
      this.style.scrollBehavior = 'auto';
      this.scrollLeft -= this.loopWidth;
    }

    this.style.scrollBehavior = 'smooth';
    this.scrollLeft += stride;
  }
}

if (!customElements.get('deal-marquee')) {
  customElements.define('deal-marquee', DealMarquee);
}
